import type { ReactElement } from "react";
import { Link, useLocation } from "react-router";

import { useApiData } from "../../api/use-api-data.js";
import { usePageMeta } from "../../app/use-page-meta.js";
import { ApiState, titleFor } from "../../components/api-state.js";
import { useRowLink } from "../../components/row-link.js";
import { useSearchForm } from "../../forms/use-search-form.js";

import { adminCenterApi, adminCenterUrls } from "./urls.js";

/** 検索結果の 1 行（サーバの CenterTenantRow と同じ欄）。 */
export interface CenterTenantRow {
    readonly tenantId: number;
    readonly title: string;
    readonly company: string;
    readonly planLabel: string;
    readonly qty: number;
    /** 月額（サーバが作る）。<b>無償利用・月額以外の契約は金額でなく「無償利用の契約」「月額以外の契約」</b>（Implem.Portal.Backend の docs/features/26 §1）。 */
    readonly amountLabel: string;
    readonly cancelled: boolean;
    /** 最終ログ（その契約のアクティビティログの最後の日時。日本時間）。<b>ログが無ければサーバが「—」を返す。</b> */
    readonly lastActivityLabel: string;
}

/** 契約を探す画面（<c>GET /api/admin-center/tenants</c>。サーバの CenterSearchViewModel と同じ欄）。 */
export interface CenterSearch {
    readonly keyword: string | null;
    /** 選んだプラン。プランの名前か <c>notFree</c>（既定）・<c>all</c>。<b>サーバは空で送られても <c>notFree</c> を返す。</b> */
    readonly planName: string | null;
    /** TenantSearchStatus の数値（0 = 利用中・1 = 解約済み・2 = すべて）。 */
    readonly status: number | null;
    readonly paymentOnInvoice: boolean | null;
    readonly rows: readonly CenterTenantRow[];
    /** 選べるプラン。<b>料金定義から出す</b>（画面に名前を書かない）。 */
    readonly plans: readonly { readonly name: string; readonly displayName: string }[];
    readonly filled: boolean;
    readonly narrowed: boolean;
}

/**
 * プランの名前の代わりに送る値（サーバの TenantSearchPlans と同じ綴り）。
 * <b>既定は「フリープラン以外」</b>——何も付けない URL もサーバがフリープラン以外として扱う。
 */
export const planChoices = { notFree: "notFree", all: "all" } as const;

/** 届いたプランの選択。空なら既定（フリープラン以外）。 */
const planOf = (search: CenterSearch): string => search.planName ?? planChoices.notFree;

/** TenantSearchStatus（Core）の値。<b>並びと数値は C# の列挙と同じ。</b> */
const statuses = [
    { value: 0, label: "利用中" },
    { value: 1, label: "解約済み" },
    { value: 2, label: "すべて" },
] as const;

function SearchForm({ search }: { readonly search: CenterSearch }): ReactElement {
    const form = useSearchForm(adminCenterUrls.tenants, {
        Keyword: search.keyword ?? "", PlanName: planOf(search), Status: String(search.status ?? 0),
        PaymentOnInvoice: search.paymentOnInvoice === null ? "" : String(search.paymentOnInvoice),
    });

    return (
        <section className="card mb-4 p-6">
            <form method="get" {...form.props}>
                <div className="mb-3">
                    <label htmlFor="Keyword" className="form-label">語で探す</label>
                    <div className="flex flex-wrap gap-2">
                        <input
                            id="Keyword" name="Keyword" className="form-input grow" autoComplete="off"
                            defaultValue={search.keyword ?? ""}
                            placeholder="企業名・システム名・契約 ID・利用者のメールアドレス"
                        />
                    </div>
                    <p className="form-help">
                        利用者のメールアドレスでも探せます（問い合わせは名前ではなくアドレスで来るため）。
                    </p>
                </div>

                <div className="flex flex-wrap items-end gap-3">
                    <div>
                        <label htmlFor="PlanName" className="form-label">プラン</label>
                        <select id="PlanName" name="PlanName" className="form-input" defaultValue={planOf(search)}>
                            <option value={planChoices.all}>すべてのプラン</option>
                            <option value={planChoices.notFree}>フリープラン以外</option>
                            {search.plans.map((plan) => <option key={plan.name} value={plan.name}>{plan.displayName}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="Status" className="form-label">状態</label>
                        <select id="Status" name="Status" className="form-input" defaultValue={String(search.status ?? 0)}>
                            {statuses.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    <div>
                        <label htmlFor="PaymentOnInvoice" className="form-label">お支払方法</label>
                        <select
                            id="PaymentOnInvoice" name="PaymentOnInvoice" className="form-input"
                            defaultValue={search.paymentOnInvoice === null ? "" : String(search.paymentOnInvoice)}
                        >
                            <option value="">すべて</option>
                            <option value="true">請求書払い</option>
                            <option value="false">カード払い</option>
                        </select>
                    </div>
                    {/* 送るボタンとクリアはひとまとまりにする（折り返すときも一緒に次の行へ落ちる。片方だけ別の行に分かれない） */}
                    <div className="flex gap-3">
                        <button type="submit" className="btn-primary">検索</button>
                        {/* **クリアは「条件の無い URL を開く」。**type="reset" は描いたときの値に戻るだけ */}
                        <Link to={adminCenterUrls.tenants} className="btn-secondary">クリア</Link>
                    </div>
                </div>
                <p className="form-help">
                    <strong className="text-ink-800">既定は「利用中」かつ「フリープラン以外」だけ</strong>です。
                    解約済みの契約も行として残っています。フリープランの契約はプランを「すべてのプラン」にすると出ます。
                </p>
            </form>
        </section>
    );
}

function Results({ search }: { readonly search: CenterSearch }): ReactElement {
    const rowLink = useRowLink();
    // **既定でフリープランを除いていることを件数の横にも出す。**気付かないと「フリープランの契約が無い」と読める
    const notFree = planOf(search) === planChoices.notFree;

    if (search.rows.length === 0) {
        return (
            <div className="empty-state text-sm text-slate-500">
                当たる契約がありませんでした。
                {search.narrowed ? "絞り込みを緩めてお試しください（状態を「すべて」にすると解約済みも含みます）。" : null}
                {notFree ? "フリープランの契約は除いています（プランを「すべてのプラン」にすると含みます）。" : null}
            </div>
        );
    }

    return (
        <>
            <p className="mb-3 text-sm text-slate-500">
                {search.rows.length} 件{notFree ? "（フリープランを除く）" : null}{search.filled ? "（上限。絞り込んでください）" : null}
            </p>
            <div className="table-card">
                <div className="table-scroll">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th scope="col" className="cell-number">契約 ID</th>
                                <th scope="col">システム名</th>
                                <th scope="col">企業名</th>
                                <th scope="col">プラン</th>
                                <th scope="col" className="cell-number">人数</th>
                                <th scope="col" className="cell-number">月額（税込）</th>
                                {/* **並びは API が決める**（最終ログの新しい順。ログの無い契約は後ろ） */}
                                <th scope="col">最終ログ</th>
                                <th scope="col"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {search.rows.map((row) => {
                                const href = adminCenterUrls.tenant(row.tenantId);
                                return (
                                    // 行ごと押せる。**「開く」は残す**——キーボードと読み上げはそちらを辿る
                                    <tr key={row.tenantId} data-row-href={href} onClick={rowLink(href)}>
                                        <td className="cell-number text-slate-500">{row.tenantId}</td>
                                        {/* 探しているのはたいていシステム名。**ここだけ太くする** */}
                                        <td className="font-medium text-ink-800">
                                            {row.title}
                                            {row.cancelled ? <> <span className="badge-muted">解約済み</span></> : null}
                                        </td>
                                        <td>{row.company}</td>
                                        <td>{row.planLabel}</td>
                                        <td className="cell-number">{row.qty}</td>
                                        <td className="cell-number">{row.amountLabel}</td>
                                        <td className="whitespace-nowrap text-slate-500">{row.lastActivityLabel}</td>
                                        <td className="cell-actions">
                                            <Link to={href} className="btn-secondary btn-sm">開く</Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

/** 契約情報を探す。<b>条件は URL のクエリ。</b> */
export function TenantSearchPage(): ReactElement | null {
    const { search } = useLocation();
    const data = useApiData<CenterSearch>(adminCenterApi.tenants(search));
    usePageMeta(titleFor(data, "契約情報"));

    if (data.state !== "ready") {
        return <ApiState data={data} what="契約情報" />;
    }

    return (
        <div className="page max-w-5xl">
            <p className="mb-4"><Link to={adminCenterUrls.menu} className="btn-secondary btn-sm">管理センターへ戻る</Link></p>
            <div className="page-header">
                <div>
                    <h1 className="page-title">契約情報</h1>
                    <p className="page-subtitle">探して開くと、契約の状況・請求の設定・利用者を確認できます。</p>
                </div>
            </div>

            <SearchForm search={data.value} />
            <Results search={data.value} />
        </div>
    );
}
